import React, {
  useState,
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle,
  useCallback
} from "react";
import "./SideArche.css";
import rt from "../../images/rt.svg";

const SideResult = forwardRef(({ onMenuItemClick, resultDetails, sideclass }, ref) => {
  const [activeItem, setActiveItem] = useState(0);
  const [isMobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [topOffset, setTopOffset] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const [sidebarHeight, setSidebarHeight] = useState(800);

  const sidebarRef = useRef(null);
  const containerRef = useRef(null);
  const isScrollingRef = useRef(false);
  const timeoutRef = useRef(null);
  const bottomPadding = 0;

  const stableSetActiveIndex = useCallback((index) => {
    if (isScrollingRef.current) return; 
    
    setActiveItem(prevIndex => {
      if (prevIndex !== index) {
        // Clear any existing timeout
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
        
        timeoutRef.current = setTimeout(() => {
          scrollToActiveItem(index);
        }, 100);
      }
      return index;
    });
  }, []);

  useImperativeHandle(ref, () => ({
    setActiveIndex: stableSetActiveIndex
  }), [stableSetActiveIndex]);

  const scrollToActiveItem = useCallback((index) => {
   
    if (!sidebarRef.current || !containerRef.current) {
      console.warn('Sidebar refs not ready');
      return;
    }

    const container = containerRef.current;
    const items = container.querySelectorAll("li");
    
    if (!items || items.length === 0) {
      console.warn('No sidebar items found');
      return;
    }

    const activeEl = items[index];
    if (!activeEl) {
      console.warn(`Active element at index ${index} not found`);
      return;
    }

    try {

      const containerRect = container.getBoundingClientRect();
      const elementRect = activeEl.getBoundingClientRect();
      
      if (!containerRect || !elementRect) {
        console.warn('Could not get element positions');
        return;
      }
      
      const isVisible = (
        elementRect.top >= containerRect.top - 10 && 
        elementRect.bottom <= containerRect.bottom + 10
      );

      if (!isVisible) {
        isScrollingRef.current = true;
        
        if (index === 0) {
          // If first item, scroll to top
          container.scrollTo({ top: 0, behavior: "smooth" });
        } else {
          // Calculate optimal scroll position
          const containerHeight = container.clientHeight;
          const elementOffsetTop = activeEl.offsetTop;
          
          // Position active item in the upper portion of visible area
          let targetScrollTop = elementOffsetTop - (containerHeight * 0.2);

          const maxScrollTop = container.scrollHeight - containerHeight - bottomPadding;
          targetScrollTop = Math.max(0, Math.min(targetScrollTop, maxScrollTop));

          container.scrollTo({
            top: targetScrollTop,
            behavior: "smooth"
          });
        }

        // Reset scrolling flag after animation
        setTimeout(() => {
          isScrollingRef.current = false;
        }, 1000);
      } else {
        isScrollingRef.current = false;
      }
    } catch (error) {
      console.error('Error in scrollToActiveItem:', error);
      isScrollingRef.current = false;
    }
  }, [bottomPadding]);

  const handleMenuItemClick = useCallback((index) => {
    isScrollingRef.current = true;
    setActiveItem(index);
    setMobileMenuOpen(false);
    
    // Add small delay to ensure state updates
    setTimeout(() => {
      if (onMenuItemClick && typeof onMenuItemClick === 'function') {
        onMenuItemClick(index);
      }
      isScrollingRef.current = false;
    }, 100);
  }, [onMenuItemClick]);

  // Initialize mobile detection safely
  useEffect(() => {
    const checkMobile = () => {
      try {
        setIsMobile(window.innerWidth <= 768);
      } catch (error) {
        console.warn('Error checking mobile:', error);
        setIsMobile(false);
      }
    };
    
    checkMobile();
  }, []);

  // Calculate dynamic sidebar height with error handling
  const calculateSidebarHeight = useCallback(() => {
    try {
      const viewportHeight = window.innerHeight || 800;
      const headerHeight = topOffset || 0;
      const availableHeight = viewportHeight - headerHeight - 40; // More margin for production
      
      return Math.min(Math.max(availableHeight, 300), 800);
    } catch (error) {
      console.warn('Error calculating height:', error);
      return 600; // Fallback height
    }
  }, [topOffset]);

  useEffect(() => {
    let rafId = null;
    let isThrottling = false;

    const updateDimensions = () => {
      try {
        const dashboard = document.querySelector(".dashboard-header");
        const dashboardHeight = dashboard?.getBoundingClientRect?.()?.height || 0;
        const scrollY = window.scrollY || window.pageYOffset || 0;
        const viewportHeight = window.innerHeight || 800;
        const docHeight = document.documentElement?.scrollHeight || document.body?.scrollHeight || 0;
        const distanceFromBottom = Math.max(0, docHeight - (scrollY + viewportHeight));

        // Calculate sticky top offset
        let offset = dashboardHeight + 20;
        if (distanceFromBottom < 100) {
          offset = Math.max(0, offset - (100 - distanceFromBottom));
        }
        setTopOffset(offset);

        // Update sidebar height
        const newHeight = calculateSidebarHeight();
        setSidebarHeight(newHeight);
      } catch (error) {
        console.warn('Error updating dimensions:', error);
      }
    };

    const updateMobile = () => {
      try {
        setIsMobile(window.innerWidth <= 768);
      } catch (error) {
        console.warn('Error updating mobile:', error);
      }
    };

    const throttledUpdate = () => {
      if (isThrottling) return;
      
      isThrottling = true;
      rafId = requestAnimationFrame(() => {
        updateDimensions();
        isThrottling = false;
      });
    };

    // Initial setup
    updateDimensions();
    updateMobile();
    
    // Add event listeners with passive option for better performance
    const scrollOptions = { passive: true, capture: false };
    const resizeOptions = { passive: true };
    
    if (window.addEventListener) {
      window.addEventListener("scroll", throttledUpdate, scrollOptions);
      window.addEventListener("resize", updateMobile, resizeOptions);
      window.addEventListener("resize", updateDimensions, resizeOptions);

      document.body.style.setProperty("overflow-x", "visible", "important");
      document.documentElement.style.setProperty("overflow-x", "visible", "important");
    }

    return () => {
      if (rafId) {
        cancelAnimationFrame(rafId);
      }
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      
      if (window.removeEventListener) {
        window.removeEventListener("scroll", throttledUpdate, scrollOptions);
        window.removeEventListener("resize", updateMobile, resizeOptions);
        window.removeEventListener("resize", updateDimensions, resizeOptions);
      }
    };
  }, [calculateSidebarHeight]);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  // Filter valid items safely
  const validItems = React.useMemo(() => {
    if (!resultDetails || !Array.isArray(resultDetails)) {
      return [];
    }
    
    return resultDetails.filter((item) => 
      item && 
      item.title && 
      typeof item.title === 'string' && 
      item.title.trim() !== ""
    );
  }, [resultDetails]);

  if (!validItems.length) {
    return null; // Don't render if no valid items
  }

  return (
    <>
      {isMobile && (
        <button
          className="side-result-toggle"
          onClick={() => setMobileMenuOpen(prev => !prev)}
          aria-label="Toggle navigation menu"
          type="button"
        >
          ☰
        </button>
      )}

      <div
        ref={sidebarRef}
        className={`arche-side side-result ${sideclass || ''} ${isMobileMenuOpen ? "open" : ""}`}
        style={{
          position: "sticky",
          top: `${Math.max(topOffset, 140)}px`,
          // height: `${sidebarHeight}px`,
          maxHeight: "calc(100vh - 30px)",
          overflow: "hidden",
          zIndex: 100,
          willChange: "transform" // Optimize for animations
        }}
      >
        <div
          ref={containerRef}
          style={{
            height: "100%",
            overflowY: "auto",
            overflowX: "hidden",
            scrollbarWidth: "thin",
            msOverflowStyle: "none",
            WebkitOverflowScrolling: "touch" // iOS smooth scrolling
          }}
          className="sidebar-scroll-container"
        >
          <ul style={{ 
            paddingBottom: `${bottomPadding}px`,
            margin: 0,
            listStyle: "none",
            padding: 0
          }}>
            {validItems.map((item, index) => (
              <li
                key={`sidebar-item-${item.id || index}-${index}`}
                className={activeItem === index ? "showNow" : ""}
                onClick={() => handleMenuItemClick(index)}
                style={{
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                  userSelect: "none"
                }}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleMenuItemClick(index);
                  }
                }}
              >
                <span className="menu-title">{item.title}</span>
                <span className="menu-icon">
                  <img src={rt} alt="Navigate" />
                </span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </>
  );
});

SideResult.displayName = 'SideResult';

export default SideResult;